import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";

const CampaignSection = styled.section`
  padding: 5rem 0;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const HeaderLeft = styled.div`
  opacity: 0;
  transform: translateX(-50px);
  transition: all 0.8s ease;
  transition-delay: 0.3s;

  .visible & {
    opacity: 1;
    transform: translateX(0);
  }
`;

const HeaderRight = styled.div`
  opacity: 0;
  transform: translateX(50px);
  transition: all 0.8s ease;
  transition-delay: 0.3s;

  .visible & {
    opacity: 1;
    transform: translateX(0);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatsContainer = styled.div`
  margin-top: auto;
  padding-top: 1.5rem;
`;

const StatColumn = styled.div`
  text-align: left;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  color: ${(props) => props.theme.colors.dark};
  font-weight: 500;
`;

const ProgressBar = styled.div`
  background: #e0e0e0;
  height: 1.5rem;
  border-radius: 14px;
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const Percentage = styled.div`
  color: ${(props) => props.theme.colors.light};
  padding-inline-end: 15px;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;
const ProgressFill = styled.div`
  background: #b5873a;
  display: flex;
  font-weight: 500;
  justify-content: center;
  font-size: 11px;
  height: 100%;
  width: ${(props) => props.progress}%;
`;

const Category = styled.h4`
  color: #b5873a;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: ${(props) => props.theme.colors.dark};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ViewAllButton = styled(Link)`
  background: #b5873a;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s ease;
  text-decoration: none;

  &:hover {
    background: #9e7431;
  }
`;

const CampaignGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CampaignCard = styled.div`
  background: white;
  border-radius: 30px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transform: translateX(50px);
  transition: all 0.8s ease;
  transition-delay: ${(props) => props.index * 0.2}s;
  display: flex;
  flex-direction: column;
  height: 100%;

  &.visible {
    opacity: 1;
    transform: translateX(0);
  }
`;

const CampaignImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const CampaignTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

const CampaignMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  color: #666;
  font-size: 0.9rem;

  div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const CampaignDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;


const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const DonateButton = styled(Link)`
  background: #b5873a;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s ease;
  text-decoration: none;
  flex: 1;
  text-align: center;

  &:hover {
    background: #9e7431;
  }
`;

const DetailsButton = styled(Link)`
  background: transparent;
  color: ${(props) => props.theme.colors.dark};
  border: 1px solid #e0e0e0;
  padding: 0.8rem 1.5rem;
  border-radius: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  flex: 1;
  text-align: center;

  &:hover {
    background: #f5f5f5;
  }
`;

function RecentCampaign() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const campaigns = [
    {
      id: 1,
      title: "Heartbeat Journey: Spreading Hope, One Step at a Time",
      description:
        "A long-term initiative to bring positive change across Morocco through education, healthcare, and community support.",
      image: "/images/sliders/SLIDE_HBJ_main.webp",
      location: "Across 12 regions, Morocco",
      date: "November 2024 - 2031",
      path: "project/1",
    },
    {
      id: 2,
      title: "1st Action: Pilot Project in Douar Al Byadna",
      description:
        "Our successful pilot project that laid the foundation for the Heartbeat Journey, bringing hope and education to a rural community.",
      image: "/images/HeartbeatJourney-Actions/hb-v1/SLIDE_HBJv1_sans.webp",
      location: "Marrakech-Safi Region",
      date: "Nov 15-17, 2023",
      path: "project/2",
    },
  ];

  return (
    <CampaignSection ref={sectionRef} className={isVisible ? "visible" : ""}>
      <Container>
        <Header>
          <HeaderLeft>
            <Category>RECENT CAMPAIGN</Category>
            <Title>
              Help us build brighter futures
              <br />
              for the next generations.
            </Title>
          </HeaderLeft>
          <HeaderRight>
            <ViewAllButton to="/heartbeat-journey">View All Campaigns</ViewAllButton>
          </HeaderRight>
        </Header>

        <CampaignGrid>
          {campaigns.map((campaign, index) => (
            <CampaignCard
              key={campaign.id}
              index={campaign.id}
              className={isVisible ? "visible" : ""}
            >
              <CampaignImage src={campaign.image} alt={campaign.title} width="800" height="250" loading="lazy" />
              <CardContent>
                <CampaignTitle>{campaign.title}</CampaignTitle>
                <CampaignMeta>
                  <div>
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {campaign.location}
                  </div>
                  <div>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {campaign.date}
                  </div>
                </CampaignMeta>

                <CampaignDescription>{campaign.description}</CampaignDescription>

                <StatsContainer>
                  {campaign.raised && campaign.target && campaign.progress && (
                    <>
                      <StatsGrid>
                        <StatColumn>
                          <StatLabel>Raised</StatLabel>
                          <StatValue>{campaign.raised} DH</StatValue>
                        </StatColumn>
                        <StatColumn>
                          <StatLabel>Target</StatLabel>
                          <StatValue>{campaign.target} DH</StatValue>
                        </StatColumn>
                      </StatsGrid>
                      <ProgressBar>
                        <ProgressFill progress={campaign.progress}>
                          <Percentage>{campaign.progress}%</Percentage>
                        </ProgressFill>
                      </ProgressBar>
                    </>
                  )}
                </StatsContainer>

                <ButtonGroup>
                  <DonateButton to={"/donate"}>
                    Donate Now
                  </DonateButton>
                  <DetailsButton to={`/heartbeat-journey/${campaign.path}`}>
                    Program Details
                  </DetailsButton>
                </ButtonGroup>
              </CardContent>
            </CampaignCard>
          ))}
        </CampaignGrid>
      </Container>
    </CampaignSection>
  );
}

export default RecentCampaign;
